import { apiClient } from './api-client';
import { Booking, ContactInfo, CommunicationRecord } from '@/types';

export interface BookingConfirmationRequest {
  bookingId: string;
  guestContact: ContactInfo;
  includeCheckInForm?: boolean;
}

export interface BookingConfirmationResponse {
  success: boolean;
  message: string;
  communicationRecords: CommunicationRecord[];
  checkInFormUrl?: string;
}

export interface CheckInFormUrlRequest {
  bookingId: string;
  expirationHours?: number;
}

export interface CheckInFormUrlResponse {
  formUrl: string;
  expirationHours: number;
  message: string;
}

export interface BookingConfirmationStatus {
  bookingId: string;
  confirmationsSent: {
    email: boolean;
    whatsapp: boolean;
  };
  lastAttempt?: string;
  retryCount: number;
  checkInFormGenerated: boolean;
  checkInFormUrl?: string;
  checkInFormExpiration?: string;
}

export class BookingConfirmationService {
  /**
   * Send booking confirmation messages to guest
   */
  static async sendBookingConfirmation(request: BookingConfirmationRequest): Promise<BookingConfirmationResponse> {
    try {
      const response = await apiClient.post<BookingConfirmationResponse>(
        '/bookings/send-confirmation',
        request
      );
      return response as any;
    } catch (err) {
      console.error('Failed to send booking confirmation:', err);
      throw err;
    }
  }

  /**
   * Generate check-in form URL for a booking
   */
  static async generateCheckInFormUrl(request: CheckInFormUrlRequest): Promise<CheckInFormUrlResponse> {
    try {
      const response = await apiClient.post<CheckInFormUrlResponse>(
        '/check-in-form/generate-url',
        request
      );
      return response as any;
    } catch (err) {
      console.error('Failed to generate check-in form URL:', err);
      throw err;
    }
  }

  /**
   * Get booking confirmation status
   */
  static async getConfirmationStatus(bookingId: string): Promise<BookingConfirmationStatus> {
    try {
      // Get both booking details and communication history
      const [bookingResponse, communicationsResponse] = await Promise.all([
        apiClient.get<any>(`/bookings/${bookingId}/confirmation-status`),
        this.getCommunicationHistory(bookingId)
      ]);
      
      const data = bookingResponse.data || bookingResponse;
      const communications = communicationsResponse;
      
      // Analyze communication records to determine actual status
      const emailCommunications = communications.filter(c => c.type === 'email');
      const whatsappCommunications = communications.filter(c => c.type === 'whatsapp');
      
      const emailSent = emailCommunications.some(c => c.status === 'sent' || c.status === 'delivered');
      const whatsappSent = whatsappCommunications.some(c => c.status === 'sent' || c.status === 'delivered');
      
      // Get the latest communication timestamp
      const lastCommunication = communications.length > 0 
        ? communications.sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())[0]
        : null;
      
      const transformedStatus: BookingConfirmationStatus = {
        bookingId: data.bookingId || bookingId,
        confirmationsSent: {
          email: emailSent,
          whatsapp: whatsappSent,
        },
        lastAttempt: lastCommunication?.sentAt || data.lastUpdated,
        retryCount: 0, // Default value since not provided by API
        checkInFormGenerated: Boolean(data.checkInFormUrl || data.checkInFormGenerated),
        checkInFormUrl: data.checkInFormUrl,
        checkInFormExpiration: data.checkInFormExpiration,
      };
      
      return transformedStatus;
    } catch (err) {
      console.error('Failed to get confirmation status:', err);
      throw err;
    }
  }

  /**
   * Retry failed booking confirmations
   */
  static async retryBookingConfirmation(bookingId: string, listingId?: string): Promise<BookingConfirmationResponse> {
    try {
      // Use the retry automation endpoint with confirmations task
      const url = listingId 
        ? `/bookings/${bookingId}/retry-automation?listingId=${listingId}`
        : `/bookings/${bookingId}/retry-automation`;
        
      const response = await apiClient.post<BookingConfirmationResponse>(
        url,
        { retryTasks: ['confirmations'] }
      );
      return response as any;
    } catch (err) {
      console.error('Failed to retry booking confirmation:', err);
      throw err;
    }
  }

  /**
   * Send manual reminder to guest
   */
  static async sendManualReminder(bookingId: string, message?: string): Promise<BookingConfirmationResponse> {
    try {
      const response = await apiClient.post<BookingConfirmationResponse>(
        `/bookings/${bookingId}/send-reminder`,
        { customMessage: message }
      );
      return response as any;
    } catch (err) {
      console.error('Failed to send manual reminder:', err);
      throw err;
    }
  }

  /**
   * Get communication history for a booking
   */
  static async getCommunicationHistory(bookingId: string): Promise<CommunicationRecord[]> {
    try {
      const response = await apiClient.get<any>(
        `/bookings/${bookingId}/communications`
      );
      
      // Handle different response structures
      const data = response.data || response;
      
      // The backend returns { communicationLog: [...] }
      if (data.communicationLog && Array.isArray(data.communicationLog)) {
        return data.communicationLog;
      } else if (data.communications && Array.isArray(data.communications)) {
        return data.communications;
      } else if (Array.isArray(data)) {
        return data;
      } else {
        console.warn('Unexpected communication history response structure:', data);
        return [];
      }
    } catch (err) {
      console.error('Failed to get communication history:', err);
      // Return empty array instead of throwing to prevent UI errors
      return [];
    }
  }

  /**
   * Update booking confirmation preferences
   */
  static async updateConfirmationPreferences(
    bookingId: string, 
    preferences: { 
      autoSendConfirmation?: boolean;
      includeCheckInForm?: boolean;
      reminderTiming?: string;
    }
  ): Promise<void> {
    try {
      await apiClient.put(`/bookings/${bookingId}/confirmation-preferences`, preferences);
    } catch (err) {
      console.error('Failed to update confirmation preferences:', err);
      throw err;
    }
  }
}

export default BookingConfirmationService;